import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { animate, AnimatePresence, motion, useMotionValue, useTransform } from "framer-motion";
import styled from "styled-components";
import { ThemeRoot, ThemeName, t } from "./theme";
import { Card, ContentGrid, ContentGrid2, HeaderActionsContainer, HeaderContainer, HeaderInfoContainer, HeaderSubtitle, HeaderTitleContainer, IconContainer, Panel, Column } from "./styled";
import { BreadcrumbHeader } from "../../components/UI/BreadcrumbHeader";
import { InlineEditableInput } from "../../components/inputs/InlineEditableInput";
import { CollabLockedField } from "../../components/collab/CollabLockedField";
import { ButtonGhostV2, getVal, icons, LoaderCircleV2, LoaderOverlay, ModalConfirm, SkeletonLoader, TooltipV2, typoV2_CSS, UI_BODY_SEMIBOLD_SM_DF, UI_BODY_SM_DF, UI_CAPTION, UI_CAPTION_SEMIBOLD, useBannerContext } from "oolib";
import TplActions from "../../components/generalUI/TplActions";
import { getPublishedDocState } from "../../utils/getPublishedDocState";
import { useTriggerAIAutoAnnotate, useWipeDocumentAnnotations } from "../../utils/react-query-hooks/aiAnnotation";
import { useSignalPicker } from "../../components/SignalPickerModal";
import { __CheckAccessToModule } from "../../utils/accessControl";
import { Tabs } from "./comps/AnimatedTabs";
import { AnnoCard, AnnoCardProps } from "./comps/AnnoCard";
import { ButtonTertiaryCompact } from "./comps/Button";
import { DocumentTagsCard, DocumentTagsField } from "./comps/DocumentTagsCard";
import { FieldsRenderer } from "./comps/FieldsRenderer";
import { DistributionCard, DistributionCardContent } from "./comps/DistributionCard";
import { ViewControlsModal } from "./comps/ViewControlsModal";
import { ChartBarHorizontalIcon, Faders } from "@phosphor-icons/react";
import { useGetContribution } from "../../utils/react-query-hooks/contributions";
import { useDocCollaboration } from "../../utils/docCollaboration/useDocCollaboration";
import { useAnnotationEditPipeline } from "../../utils/annotationEditPipeline/useAnnotationEditPipeline";
import { DocCollaborationContext } from "../../contexts/docCollaborationContext";
import { AnnotationEditPipelineContext } from "../../contexts/annotationEditPipelineContext";
import { CollabEditContext } from "../../contexts/collabEditContext";
import { useContentDocCollabSession } from "../../utils/collab/useContentDocCollabSession";
import { isContentCollabEnabled } from "../../utils/collab/isContentCollabEnabled";
import { getQueryParam } from "../../utils/general";
import { useScrollToDeepLinkTarget } from "./utils/useScrollToDeepLinkTarget";
import { useAnnoOrderIndex } from "./utils/useAnnoOrderIndex";
import { useAnnotationsCsvDownload } from "./utils/useAnnotationsCsvDownload";
import ErrorStates from "../../components/ErrorStates";
import { useGetDocumentSummary } from "../../components/analysisModules/AnnotationExplorer/comps/Summary/DocumentSummary";
import { useGetQueryData } from "../../utils/react-query-hooks/general";
import { isFeatureDisabledOnProd } from "../../utils/isFeatureDisabledOnProd";
import { getTenant } from "@/TENANT_CONFIGS";
import { useGetTypeCategoryAndTags } from "../../components/inputs/DynamicTagsInputs/utils";
import { NAMED_ENTITIES_KEY } from "../../utils/aiTaxonomyConstants";
import { useLocalStorage } from "../../utils/customHooks/useLocalStorage";
import { getRoutePathToPublishedContent, blockRegistry, isTplAnnotationEnabled, mergeAnnoDataIntoAnnotationsTags, extractAllBlocksFromTpl } from "@okf/ootils";
import { COMP_ICON_MAP, COMP_ICON_WEIGHT_MAP } from "../../utils/getCompIconMap";
import { AnnoEditorRegistryProvider, useAnnoEditorRegistry } from "../../components/inputs/TextEditor/LexicalTextEditor/plugins/AnnoPlugin/annoEditorRegistry";

// The Document Tags card surfaces only these field types (the same set the
// data-import flow recognises as tag-like / categorical / temporal metadata).
const DOC_TAG_COMPS = [
  "DatePicker",
  "DateTimePicker",
  "DropdownSingle",
  "DropdownMulti",
  "TagsInputSingle",
  "TagsInputMulti",
] as const;
const DOC_TAG_COMP_SET = new Set<string>(DOC_TAG_COMPS);

const isTagPath = (p?: string): boolean => !!p && (p.startsWith("tags.") || p === "tags");

// Where every content doc carries its title — the header reads and writes here.
const TITLE_VALUE_PATH = "main.title";

// Dispatch gate (reuses the existing `tpl.category` layout-type field that
// PublishedPageLayoutRenderer dispatches on): the layouts whose structure the
// V3 published page is built + validated for. The body renderer itself is
// layout-agnostic (blockMapper drills any structure) — this set is the rollout
// control for which tpls get the new V3 UI.
const V3_SUPPORTED_LAYOUTS = new Set<string>([
  "knowledgeResources2",
  "applicationFormsStyle1",
]);

const formatDocumentDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const oneYearMs = 365 * 24 * 60 * 60 * 1000;
  if (diffMs >= oneYearMs) {
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const seconds = Math.max(Math.floor(diffMs / 1000), 0);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  if (months >= 1) return rtf.format(-months, "month");
  if (weeks >= 1) return rtf.format(-weeks, "week");
  if (days >= 1) return rtf.format(-days, "day");
  if (hours >= 1) return rtf.format(-hours, "hour");
  if (minutes >= 1) return rtf.format(-minutes, "minute");
  return "just now";
};

const { CaretRight, Sparkle } = icons;

type MappedAnno = Pick<AnnoCardProps, "quote" | "gist" | "observationTitle" | "actorName" | "tags" | "emoji" | "details"> & { id: string };

// Imperative bridge for filter dimming. Lives inside <AnnoEditorRegistryProvider>
// (the registry hook only resolves below the provider, which the page renders in
// its own JSX). When a tag filter is applied, fades every editor mark whose
// annotation is filtered out, via the registry's setDimmedAnnos (one batched
// broadcast per filter change) — reactive editor props are swallowed by
// ViewBlockGenerator's memo, so this has to be imperative.
const AnnoFilterDimmer: React.FC<{
  annos: MappedAnno[];
  activeAnnoIds: Set<string>;
  hasFilter: boolean;
}> = ({ annos, activeAnnoIds, hasFilter }) => {
  const registry = useAnnoEditorRegistry();
  useEffect(() => {
    if (!registry) return;
    // Marks live inside <ThemeRoot>, so the var(--grey10) CSS variable resolves
    // on them via inheritance. Passed in because the shared AnnoPlugin has no
    // access to the V3 theme palette (oolib's `colors` has no grey10).
    const dimColor = t("grey10");
    // Build the complete dimmed set once and broadcast it in a single call, so
    // every editor repaints exactly once per filter change (rather than once per
    // annotation). Empty set == filter cleared, which resets the marks.
    const dimmedIds = new Set<string>();
    if (hasFilter) {
      for (const a of annos) {
        if (!activeAnnoIds.has(a.id)) dimmedIds.add(a.id);
      }
    }
    registry.setDimmedAnnos(dimmedIds, dimColor);
    return () => registry.setDimmedAnnos(new Set<string>(), dimColor);
  }, [registry, annos, activeAnnoIds, hasFilter]);
  return null;
};

const HighlightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;


const SummaryText = styled(UI_BODY_SM_DF)`
  color: ${t("grey70")};
  line-height: 1.55;
`;

// Collab counterpart of ContributeTpl's H_SaveStatus autosave indicator,
// driven by the flush protocol instead of the autosave mutation: setField →
// 'saving', server flush:ok covering our pending paths → 'saved',
// flush:error → 'error' (the session hook also raises the red banner).
// 'stalled' = writes pending but the server isn't confirming (disconnected,
// or silent past the watchdog) — the edit is NOT saved yet and the label
// must not pretend a save is in normal progress.
const CollabSaveStatus = ({ saveStatus }: { saveStatus: string }) => {
  if (saveStatus === "idle") return null;
  const text =
    saveStatus === "saving"
      ? "Saving..."
      : saveStatus === "saved"
      ? "Saved!"
      : saveStatus === "stalled"
      ? "Not saved yet — reconnecting..."
      : "Save failed. Retrying...";
  return (
    <UI_CAPTION_SEMIBOLD
      style={{
        color:
          saveStatus === "error" || saveStatus === "stalled"
            ? t("redText")
            : t("grey60"),
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </UI_CAPTION_SEMIBOLD>
  );
};

// ── Detailed ("long") summary styling ───────────────────────────────────────
// V3-themed counterparts of the structured sections that V2's DocumentSummary
// renders (Context, Core Theme, Key Actors, Key Points, Insights, Implications).
// The summary payload is the same shape from useGetDocumentSummary; V3 simply
// wasn't surfacing anything beyond `shortSummary`.
const SummarySections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const SectionHeader = styled(UI_BODY_SEMIBOLD_SM_DF)`
  color: ${t("grey80")};
`;

const SectionBody = styled(UI_BODY_SM_DF)`
  color: ${t("grey70")};
  line-height: 1.55;
`;

const ActorsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const ActorCard = styled.div`
  background: ${t("grey5")};
  padding: 0.8rem;
  border-radius: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const ActorName = styled(UI_BODY_SEMIBOLD_SM_DF)`
  color: ${t("grey80")};
`;

const ActorMeta = styled(UI_CAPTION)`
  color: ${t("grey60")};
`;

const KeyPointsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const KeyPointCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding-left: 1rem;
  border-left: 2px solid ${t("grey10")};
`;

const Evidence = styled(UI_CAPTION)`
  color: ${t("grey60")};
  font-style: italic;
`;

const SummaryList = styled.ul`
  margin: 0;
  padding-left: 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const SummaryListItem = styled.li`
  ${typoV2_CSS.UI_BODY_SM_DF};
  list-style-type: disc;

  & > p {
    margin: 0;
  }
`;

// Inline text toggle between the short and detailed summary. Plain styled
// button so the hit target is the text itself; the label is a UI_CAPTION child
// (the button only carries the link colour + underline, which propagate to the
// caption text). Child <p> margin reset so it sits flush.
const SummaryToggleLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin-top: 0.8rem;
  color: ${t("grey80")};
  text-decoration: underline;
  cursor: pointer;

  & > * {
    margin: 0;
  }

  &:hover {
    color: ${t("grey100")};
  }
`;

// The AI panel is a fixed-height card with overflow:hidden, and the Tabs body
// grows to fit its content — so a long ("detailed") summary would overflow and
// get clipped. Bounding the summary body to the visible panel height and
// scrolling internally keeps the full text reachable.
const SummaryScroller = styled.div`
  max-height: calc(100vh - 170px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 0.4rem;
`;

// Floating View-settings button — positioned just to the LEFT of the localhost
// tenant switcher (which is fixed at bottom: 7px; right: 60px), so a higher
// `right` offset clears it.
const FloatingControlsButton = styled.button`
  position: fixed;
  bottom: 7px;
  right: 110px;
  z-index: 1000000;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${t("grey20")};
  background: ${t("surface1")};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  cursor: pointer;

  &:hover {
    background: ${t("grey5")};
  }
`;

const AnnoCardWithScroll: React.FC<{
  anno: MappedAnno;
  selected: boolean;
  onSelect: (id: string, el: HTMLElement | null) => void;
  onCollapse: () => void;
  tagDetailsExpanded: boolean;
  onToggleTagDetails: () => void;
}> = ({ anno, selected, onSelect, onCollapse, tagDetailsExpanded, onToggleTagDetails }) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  return (
    <div ref={wrapRef} data-anno-id={anno.id}>
      <AnnoCard
        dataId={anno.id}
        quote={anno.quote}
        gist={anno.gist}
        observationTitle={anno.observationTitle}
        actorName={anno.actorName}
        tags={anno.tags}
        emoji={anno.emoji}
        details={anno.details}
        selected={selected}
        onSelect={() => onSelect(anno.id, wrapRef.current)}
        onCollapse={onCollapse}
        onAddTag={() => {}}
        onAutoTag={() => {}}
        tagDetailsExpanded={tagDetailsExpanded}
        onToggleTagDetails={onToggleTagDetails}
      />
    </div>
  );
};

const AISummaryPanel: React.FC<{ summary: any; isLoading: boolean; tooShort?: boolean }> = ({
  summary,
  isLoading,
  tooShort,
}) => {
  // Render stacked SkeletonLoader rows while the summary request is in flight
  // — matches the loading pattern used elsewhere in the codebase
  // (e.g. AnnoSuggestionsPlugin/index.js:241-255). Without this the panel
  // briefly shows "Summary not available." and then snaps to the real text,
  // which reads as a real "no data" state.
  if (isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
        <SkeletonLoader style={{ width: "100%", height: "1.6rem", borderRadius: "0.4rem" }} />
        <SkeletonLoader style={{ width: "95%", height: "1.6rem", borderRadius: "0.4rem" }} />
        <SkeletonLoader style={{ width: "97%", height: "1.6rem", borderRadius: "0.4rem" }} />
        <SkeletonLoader style={{ width: "90%", height: "1.6rem", borderRadius: "0.4rem" }} />
        <SkeletonLoader style={{ width: "60%", height: "1.6rem", borderRadius: "0.4rem" }} />
      </div>
    );
  }
  if (tooShort) {
    return <SummaryText style={{ color: t("grey50") }}>Text too short to summarize.</SummaryText>;
  }
  return <AISummaryContent summary={summary} />;
};

// Renders the short summary by default and, when the payload carries the richer
// structured fields, a "View detailed summary" toggle into the long form — the
// same short/long split V2's DocumentSummary offers. Payload shape (from
// useGetDocumentSummary): { shortSummary, context, coreProblem, actors[],
// keyPoints[], insights[], implications[] }.
const AISummaryContent: React.FC<{ summary: any }> = ({ summary }) => {
  const [showDetailed, setShowDetailed] = useState<boolean>(false);

  const hasShort = !!summary?.shortSummary;
  const actors = Array.isArray(summary?.actors) ? summary.actors : [];
  const keyPoints = Array.isArray(summary?.keyPoints) ? summary.keyPoints : [];
  const insights = Array.isArray(summary?.insights) ? summary.insights : [];
  const implications = Array.isArray(summary?.implications) ? summary.implications : [];

  const hasDetailed =
    !!summary?.context ||
    !!summary?.coreProblem ||
    actors.length > 0 ||
    keyPoints.length > 0 ||
    insights.length > 0 ||
    implications.length > 0;

  if (!hasShort && !hasDetailed) {
    return <SummaryText style={{ color: t("grey50") }}>Summary not available.</SummaryText>;
  }

  // Collapsed: short summary + (optionally) the toggle into the detailed view.
  if (!showDetailed) {
    return (
      <SummaryScroller>
        {hasShort && (
          <SummaryText style={{ color: t("grey80") }}>{summary.shortSummary}</SummaryText>
        )}
        {hasDetailed && (
          <SummaryToggleLink type="button" onClick={() => setShowDetailed(true)}>
            <UI_CAPTION>View detailed summary</UI_CAPTION>
          </SummaryToggleLink>
        )}
      </SummaryScroller>
    );
  }

  // Expanded: the full structured "long" summary.
  return (
    <SummaryScroller>
      <SummarySections>
        {summary.context && (
          <SummarySection>
            <SectionHeader>Context</SectionHeader>
            <SectionBody>{summary.context}</SectionBody>
          </SummarySection>
        )}
        {summary.coreProblem && (
          <SummarySection>
            <SectionHeader>Core Theme</SectionHeader>
            <SectionBody>{summary.coreProblem}</SectionBody>
          </SummarySection>
        )}
        {actors.length > 0 && (
          <SummarySection>
            <SectionHeader>Key Actors</SectionHeader>
            <ActorsGrid>
              {actors.map((actor: any, i: number) => (
                <ActorCard key={i}>
                  {actor.name && <ActorName>{actor.name}</ActorName>}
                  {actor.role && <ActorMeta>{actor.role}</ActorMeta>}
                  {actor.description && <SectionBody>{actor.description}</SectionBody>}
                </ActorCard>
              ))}
            </ActorsGrid>
          </SummarySection>
        )}
        {keyPoints.length > 0 && (
          <SummarySection>
            <SectionHeader>Key Points</SectionHeader>
            <KeyPointsWrapper>
              {keyPoints.map((kp: any, i: number) => (
                <KeyPointCard key={i}>
                  <SectionBody>{kp.point}</SectionBody>
                  {kp.evidence && <Evidence>“{kp.evidence}”</Evidence>}
                </KeyPointCard>
              ))}
            </KeyPointsWrapper>
          </SummarySection>
        )}
        {insights.length > 0 && (
          <SummarySection>
            <SectionHeader>Insights</SectionHeader>
            <SummaryList>
              {insights.map((insight: any, i: number) => (
                <SummaryListItem key={i}>
                  <SectionBody>{insight}</SectionBody>
                </SummaryListItem>
              ))}
            </SummaryList>
          </SummarySection>
        )}
        {implications.length > 0 && (
          <SummarySection>
            <SectionHeader>Implications</SectionHeader>
            <SummaryList>
              {implications.map((implication: any, i: number) => (
                <SummaryListItem key={i}>
                  <SectionBody>{implication}</SectionBody>
                </SummaryListItem>
              ))}
            </SummaryList>
          </SummarySection>
        )}
      </SummarySections>
      <SummaryToggleLink type="button" onClick={() => setShowDetailed(false)}>
        <UI_CAPTION>View short summary</UI_CAPTION>
      </SummaryToggleLink>
    </SummaryScroller>
  );
};

type SidebarTab = "annotations" | "comments";
type RightTab = "distribution" | "summary" | "ai-chat";

const PublishedPageV3: React.FC<{
  // self-serve renders this inline (no URL params), so the content's type + id
  // can be passed directly as props instead of being read from the route.
  contentType?: string;
  contentId?: string;
  // self-serve: passed straight through to the shared TplActions (which gates +
  // renders Edit/Delete). onEdit makes edit inline; onAfterSuccess keeps delete on-route.
  selfServeActions?: {
    onEdit?: () => void;
    onAfterSuccess?: () => void;
  };
}> = ({ contentType: contentTypeProp, contentId: contentIdProp, selfServeActions } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { content_type, contentType: _ct, contentId } = useParams<{
    content_type?: string;
    contentType?: string;
    contentId?: string;
  }>();
  const contentType = contentTypeProp || _ct || content_type;
  const contributionId =
    contentIdProp || contentId || getQueryParam(location.search, "id");

  const handleBack = useCallback(() => {
    // React Router stamps an `idx` onto history state; it's 0 (or undefined in a
    // fresh tab) when the user landed here directly via a link, so there's no
    // in-app entry to pop. In that case go up to the parent dataset route
    // (/datasets/:contentType/:contentId → /datasets/:contentType) instead of
    // calling navigate(-1), which would leave the app.
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (idx) {
      navigate(-1);
    } else {
      navigate(location.pathname.replace(/\/[^/]+\/?$/, "") || "/");
    }
  }, [navigate, location.pathname]);

  const isFirst = useRef(true);
  useEffect(() => {
    isFirst.current = false;
  }, []);

  // Single source of truth for the focused-card "TAG DETAILS" expand/collapse
  // preference. Lifted here (rather than per AnnoCard) because useLocalStorage
  // is per-instance and would otherwise desync between mounted cards.
  const [tagDetailsExpanded, setTagDetailsExpanded] = useLocalStorage(
    "v3_annocard_tag_details_expanded",
    true,
  );

  const {
    data: contributionQueryData,
    status: getContributionStatus,
    error: contributionError,
  } = useGetContribution({
    contentType,
    contentId: contributionId,
    mode: "view",
    enabled: undefined,
    firstLoad: isFirst as any,
  });

  const contribution: any = contributionQueryData?.contribution;
  const tpl: any = (contributionQueryData as any)?.tpl;


  const platformConfigs: any = useGetQueryData("platformConfigs");
  const aiSummaryEnabled: boolean = !!platformConfigs?.ai?.aiSummarization?.documentSummary?.enable;
  // On collab-editing platforms the new field-lock + live-doc flow fully
  // replaces the legacy per-annotation lock (useDocCollaboration) and the
  // annotation-edit pipeline (quickUpdate writes). Disable both here so the two
  // systems never run side by side. Non-collab platforms are untouched (flag
  // off → legacy behaviour). This is the interim step before the legacy code is
  // removed outright.
  const collabModeEnabled: boolean = isContentCollabEnabled(platformConfigs);
  // Lazy gate: the AI panel lands collapsed; the summary API only fires once
  // the user expands it. Mirrors AnnoExplorer's SummaryV2 hasTriggered latch.
  const [summaryRequested, setSummaryRequested] = useState<boolean>(false);
  const { data: documentSummaryResp, status: documentSummaryStatus } = useGetDocumentSummary({
    documentId: contribution?._id,
    contentType,
    enabled: aiSummaryEnabled && !!contribution?._id && summaryRequested,
  });
  const aiSummary: any = (documentSummaryResp as any)?.data?.summary;
  const aiSummaryTooShort: boolean = !!aiSummary?.notSummarized;
  const isSummaryLoading: boolean = aiSummaryEnabled && documentSummaryStatus === "loading";

  const docCollaboration = useDocCollaboration({
    contentType,
    contentId: contribution?._id,
    // legacy per-annotation lock — off on collab platforms (field-lock replaces it)
    enabled: !collabModeEnabled,
  });

  const queryClient = useQueryClient();
  // Optimistic: patch the cached contribution the instant an annotation
  // changes, so the sidebar cards refresh without waiting for the save
  // round-trip. (The only piece carried over from the old controller.)
  const patchContributionCache = useCallback(
    (newContribution: any) => {
      queryClient.setQueriesData(
        { queryKey: ["Contributions"] } as any,
        (old: any) => {
          if (!old?.contribution || old.contribution._id !== newContribution?._id) return old;
          return { ...old, contribution: newContribution };
        },
      );
    },
    [queryClient],
  );

  const annotationEditPipeline = useAnnotationEditPipeline({
    contentType,
    contentId: contribution?._id,
    content: contribution,
    onAfterSave: () => docCollaboration.emitContentChange("annotation-changed"),
    onOptimisticChange: patchContributionCache,
  });

  // ---- Collab editing (Milestone 1: field-locked live editing) ----
  // All the session mechanics (flag + ACL gates, live doc, flush-error
  // banner, live-value merge) live in the hook — see its docstring.
  const { liveContribution, collabEditValue, collabOwnsThisDoc, collabPending, saveStatus } =
    useContentDocCollabSession({
      contentType,
      contribution,
    });

  const headerTitle: string = liveContribution?.main?.title ?? "";
  // The title is editable on exactly the same terms as any other collab field:
  // a live synced session, edit rights, and CollabLockedField handling the
  // lock. Outside collab (flag off, unpublished doc, viewer) it stays read-only
  // text, as before.
  const handleTitleChange = useCallback(
    (_k: string, newTitle: string) => {
      collabEditValue?.setField(TITLE_VALUE_PATH, newTitle);
    },
    [collabEditValue]
  );
  const author: string =
    contribution?.meta?.kp_contributed_by?.name ?? "";
  const dateCreated: string | undefined =
    contribution?.kp_date_created ?? contribution?.meta?.kp_date_created;
  const dateCreatedDisplay: string = dateCreated ? formatDocumentDate(dateCreated) : "";
  const contentTypeDisplay: string =
    tpl?.general?.content?.title ??
    contribution?.meta?.kp_content_type ??
    contentType ??
    "";

  // Tag taxonomy used as a fallback role-resolver for chip classification
  // below. In the new (thematics/attributes) system, only subTheme docs carry
  // `meta.taxonomyRole`; individual tag docs inherit the role from their
  // parent subTheme but don't store it. The persisted annotation entries
  // strip the parent chain (convertMultiValueToTagsValue copies a fixed set
  // of fields), so a tag's role can't be read straight off the entry. The
  // map below walks each tag → its parent subTheme → that subTheme's role,
  // and is consulted only when the entry's own `meta.taxonomyRole` is
  // undefined (preserving the synchronous-read fast path for entries that
  // already carry an explicit role).
  const taxonomyTagsQuery = useGetTypeCategoryAndTags({
    tagTypeProp: "tags",
    enableDataFetch: true,
  } as any);
  const taxonomySubThemesQuery = useGetTypeCategoryAndTags({
    tagTypeProp: "subThemes",
    enableDataFetch: true,
  } as any);
  const taxonomyRoleLookup = useMemo(() => {
    const map = new Map<string, string>(); // key: `${tagType}:${_id}`
    const subThemesById = new Map<string, any>();
    const subDocs: any[] = Array.isArray(taxonomySubThemesQuery.data)
      ? (taxonomySubThemesQuery.data as any[])
      : [];
    for (const st of subDocs) {
      if (!st?._id) continue;
      subThemesById.set(st._id, st);
      const role = st?.meta?.taxonomyRole;
      if (role) map.set(`subThemes:${st._id}`, role);
    }
    const tagDocs: any[] = Array.isArray(taxonomyTagsQuery.data)
      ? (taxonomyTagsQuery.data as any[])
      : [];
    for (const tagDoc of tagDocs) {
      if (!tagDoc?._id) continue;
      const parentId = tagDoc?.tags?.subThemes?.data?.[0]?._id;
      const role = parentId ? subThemesById.get(parentId)?.meta?.taxonomyRole : undefined;
      if (role) map.set(`tags:${tagDoc._id}`, role);
    }
    return map;
  }, [taxonomyTagsQuery.data, taxonomySubThemesQuery.data]);

  // Resolves a subTheme _id to the section it belongs to in the AnnoCard's
  // expanded "TAG DETAILS" view. The three sections — separated by dashed
  // dividers — are: "ner" (the Named Entities subTheme, tagId ===
  // NAMED_ENTITIES_KEY), "attribute" (meta.taxonomyRole === "attribute"), and
  // "thematic" (everything else, the default).
  //
  // Keyed by subTheme _id and built purely from the subThemes query, because
  // annotation entries already carry their own `subThemeId` + `subThemeDisplay`
  // — the per-tag taxonomy query isn't loaded on this page (returns 0 docs), so
  // the label comes off the entry and only the section needs the subTheme doc
  // (for meta.taxonomyRole / tagId, which the entry omits).
  const subThemeSectionLookup = useMemo(() => {
    const map = new Map<string, "attribute" | "thematic" | "ner">();
    const subDocs: any[] = Array.isArray(taxonomySubThemesQuery.data)
      ? (taxonomySubThemesQuery.data as any[])
      : [];
    for (const st of subDocs) {
      if (!st?._id) continue;
      const section =
        st?.tagId === NAMED_ENTITIES_KEY
          ? "ner"
          : st?.meta?.taxonomyRole === "attribute"
            ? "attribute"
            : "thematic";
      map.set(st._id, section);
    }
    return map;
  }, [taxonomySubThemesQuery.data]);

  // Faceted tag filter for the annotation list, driven by clicks on the
  // DistributionCard chart. Keyed by subThemeId: one selected tag per
  // subTheme (clicking another bar in the same subTheme replaces it; clicking
  // the active bar again clears it). Cards must match ALL selected tags
  // (AND across subThemes).
  // A topic bar (split mode) carries `annoKeys` instead: topic membership
  // lives on the clustering tree, not on the card's tags, so the filter keeps
  // the cards whose id (= annoKey) is in that set.
  const [tagFilters, setTagFilters] = useState<
    Record<string, { _id: string; tagLabel: string; subThemeLabel: string; annoKeys?: string[] }>
  >({});

  const handleSelectTag = useCallback(
    (payload: { subThemeId: string; subThemeLabel: string; _id: string; tagLabel: string; annoKeys?: string[] }) => {
      // Single active filter at a time (even across sub-themes): clicking a tag
      // replaces whatever was selected; clicking the already-active tag clears.
      setTagFilters((prev) =>
        prev[payload.subThemeId]?._id === payload._id
          ? {}
          : {
              [payload.subThemeId]: {
                _id: payload._id,
                tagLabel: payload.tagLabel,
                subThemeLabel: payload.subThemeLabel,
                ...(payload.annoKeys ? { annoKeys: payload.annoKeys } : {}),
              },
            },
      );
    },
    [],
  );

  // Single active filter at a time → the one selected tag `_id` (if any), used to
  // border its bar in the DistributionCard chart.
  const activeFilterTagId = Object.values(tagFilters)[0]?._id;

  // Consolidated annotation sidecar (the `annotations.tags` shape) that the
  // sidebar cards, prevalence bars and count all read from.
  //
  // Non-collab: the legacy annotation-edit pipeline optimistically patches
  // contribution.annotations.tags on every change, so the stored value is
  // fresh — use it directly.
  //
  // Collab: that pipeline is disabled and collab never refetches the
  // contribution, so the stored sidecar goes stale the instant an annotation
  // is created (the mark shows in the editor, but the panel/count wouldn't).
  // Recompute it live from each lexical field's annoData in liveContribution —
  // the SAME mergeAnnoDataIntoAnnotationsTags the server flush uses — so the
  // sidebar reacts immediately. liveContribution falls back to contribution for
  // unedited paths, so non-collab-editable annotation fields are still covered.
  const liveAnnotationsTags = useMemo(() => {
    if (!collabEditValue) return contribution?.annotations?.tags;
    let tags: any = {};
    extractAllBlocksFromTpl({ tpl }).forEach((block: any) => {
      const value = block?.valuePath && getVal(liveContribution, block.valuePath);
      if (value?.isLexical && value?.annoData) {
        tags = mergeAnnoDataIntoAnnotationsTags({
          prevObj: tags,
          newLexValue: value,
          thisBlockValuePath: block.valuePath,
        });
      }
    });
    return tags;
  }, [collabEditValue, liveContribution, contribution, tpl]);

  // annoDataId → gist. The gist (split-flow AI annotations: one-sentence
  // resolved summary) lives on each field's annoData entry, NOT in the
  // annotations.tags rollup the cards are otherwise built from — so walk the
  // lexical fields once and index it. Human/legacy annos have no gist and
  // simply won't be in the map.
  const annoGistIndex = useMemo(() => {
    const map = new Map<string, string>();
    // tpl is undefined until its query lands — extractAllBlocksFromTpl
    // dereferences it immediately, so bail to an empty index on early renders.
    if (!tpl || !liveContribution) return map;
    extractAllBlocksFromTpl({ tpl }).forEach((block: any) => {
      const value = block?.valuePath && getVal(liveContribution, block.valuePath);
      if (value?.isLexical && value?.annoData) {
        for (const [id, entry] of Object.entries<any>(value.annoData)) {
          // Observation refactor: read observation.desc (was entry.gist).
          // Kept feeding the same annoGistIndex → AnnoCard `gist` prop so
          // the rendered UI is unchanged.
          if (entry?.observation?.desc) map.set(id, entry.observation.desc);
        }
      }
    });
    return map;
  }, [liveContribution, tpl]);

  // annoDataId → observation.title. Same source + walk pattern as
  // annoGistIndex. Split-mode annos carry an observation.title (3-7 word
  // encapsulation of the anno's meaning); rendered in AnnoCard as a
  // semibold header line directly above the gist. Human/legacy/lite/full
  // annos won't have it and simply won't be in the map.
  const annoObservationTitleIndex = useMemo(() => {
    const map = new Map<string, string>();
    if (!tpl || !liveContribution) return map;
    extractAllBlocksFromTpl({ tpl }).forEach((block: any) => {
      const value = block?.valuePath && getVal(liveContribution, block.valuePath);
      if (value?.isLexical && value?.annoData) {
        for (const [id, entry] of Object.entries<any>(value.annoData)) {
          if (entry?.observation?.title) map.set(id, entry.observation.title);
        }
      }
    });
    return map;
  }, [liveContribution, tpl]);

  // annoDataId → actorName. Same source + walk pattern as annoGistIndex —
  // split-mode annos carry a speaker attribution (C1 reads it from the
  // transcript). Rendered as "${actorName}:" above the quote in AnnoCard.
  // Human/legacy/lite/full annos won't have it and simply won't be in the map.
  const annoActorNameIndex = useMemo(() => {
    const map = new Map<string, string>();
    if (!tpl || !liveContribution) return map;
    extractAllBlocksFromTpl({ tpl }).forEach((block: any) => {
      const value = block?.valuePath && getVal(liveContribution, block.valuePath);
      if (value?.isLexical && value?.annoData) {
        for (const [id, entry] of Object.entries<any>(value.annoData)) {
          if (entry?.actorName) map.set(id, entry.actorName);
        }
      }
    });
    return map;
  }, [liveContribution, tpl]);


  // The DistributionCard reads annotations.tags straight off `contribution`, so
  // hand it one with the live sidecar overlaid (identity-stable when non-collab,
  // i.e. when liveAnnotationsTags IS the stored value).
  const contributionForDistribution = useMemo(
    () =>
      liveAnnotationsTags === contribution?.annotations?.tags
        ? contribution
        : { ...contribution, annotations: { ...(contribution?.annotations || {}), tags: liveAnnotationsTags } },
    [contribution, liveAnnotationsTags]
  );

  // The prevalence card renders bars from annotations.tags.tags.data; with none,
  // the card itself returns null but its wrapping <Card> would still paint an
  // empty bordered shell. Gate the whole Card on that same source.
  const hasPrevalenceData =
    Array.isArray(liveAnnotationsTags?.tags?.data) &&
    liveAnnotationsTags.tags.data.length > 0;

  // Reading-order rank per annotation (annoDataId → rank), derived from the
  // Lexical mark nodes in each annotation-enabled field's editorState, in
  // template-declaration order. Drives document-order sorting of the sidebar
  // cards below (the doc-level rollup alone yields tag-walk order). See the hook.
  const annoOrderIndex = useAnnoOrderIndex(liveContribution, tpl);

  // ── Annotations CSV export ────────────────────────────────────────────────
  // Full download orchestration (build + browser-download + async summary
  // gating) lives in the hook. See utils/useAnnotationsCsvDownload.ts.
  const { handleDownloadAnnotations, downloadPending: pendingCsvExport } =
    useAnnotationsCsvDownload({
      tpl,
      liveContribution,
      aiSummary,
      aiSummaryEnabled,
      documentSummaryStatus,
      setSummaryRequested,
      contribution,
      annoOrderIndex,
    });


  const annos: MappedAnno[] = useMemo(() => {
    const annotationsObj = liveAnnotationsTags;
    if (!annotationsObj || typeof annotationsObj !== "object") return [];

    // Group fragments by annoDataId so each unique highlighted quote becomes
    // one card, with every tag applied to that quote stacked as chips.
    const grouped = new Map<string, MappedAnno>();
    const order: string[] = [];

    const ingestCollection = (collection: any) => {
      const data: any[] = Array.isArray(collection?.data) ? collection.data : [];
      const tagType: string | undefined = collection?.collectionId;
      for (const tag of data) {
        const tagLabel: string = tag?.display ?? "";
        const _id: string | undefined = tag?._id;
        // Split by `meta.taxonomyRole`: "thematic" (or unset) → bottom row,
        // "attribute" (or anything else explicit) → top row. Role is always
        // sourced from the subTheme via the taxonomyRoleLookup map — for
        // tag-typed entries the map resolves to the parent subTheme's role;
        // for subTheme-typed entries it resolves to the subTheme's own role.
        // Tags themselves no longer carry a role. Unset defaults to thematic
        // so newly seeded subThemes whose role wasn't backfilled still bucket
        // correctly.
        const resolvedRole: string =
          (tagType && _id ? taxonomyRoleLookup.get(`${tagType}:${_id}`) : undefined) ?? "thematic";
        const isAttribute = resolvedRole !== "thematic";
        // Parent subTheme label + section, for the AnnoCard expanded grouping.
        // The entry carries its own subThemeId/subThemeDisplay; the section is
        // resolved from the subTheme doc (role / NER flag) via that id.
        const subThemeId: string | undefined = tag?.subThemeId;
        const groupLabel: string | undefined = tag?.subThemeDisplay || undefined;
        const section = subThemeId ? subThemeSectionLookup.get(subThemeId) : undefined;
        const fragments: any[] = Array.isArray(tag?.fragments) ? tag.fragments : [];
        for (const frag of fragments) {
          const id: string = frag?.annoDataId ?? frag?._id ?? `${tag?._id}-${tagLabel}`;
          const quote = frag?.editorState;
          if (!grouped.has(id)) {
            order.push(id);
            grouped.set(id, { id, quote, gist: annoGistIndex.get(id), observationTitle: annoObservationTitleIndex.get(id), actorName: annoActorNameIndex.get(id), tags: [], details: [] });
          }
          const entry = grouped.get(id)!;
          if (!entry.quote && quote) entry.quote = quote;
          if (!tagLabel) continue;
          if (isAttribute) {
            // Bucket all attributes into a single unlabeled details group —
            // AnnoCard flattens detail.values into one chip row at the top.
            const detailsArr = (entry.details ?? (entry.details = []));
            let attrGroup = detailsArr.find((d) => d.label === "");
            if (!attrGroup) {
              attrGroup = { label: "", values: [], tagType };
              detailsArr.push(attrGroup);
            }
            if (!attrGroup.values.some((v: any) => (typeof v === "object" ? v._id : v) === _id || (typeof v === "object" ? v.display : v) === tagLabel)) {
              attrGroup.values.push({ display: tagLabel, _id, subThemeLabel: groupLabel, section });
            }
          } else if (!entry.tags.some((t) => t.label === tagLabel)) {
            entry.tags.push({ label: tagLabel, tagType, _id, subThemeLabel: groupLabel, section });
          }
        }
      }
    };

    // Walk arbitrarily nested namespaces — find any node that looks like a
    // collection (has a .data array of tag entries) and ingest it.
    const walk = (node: any) => {
      if (!node || typeof node !== "object") return;
      if (Array.isArray(node?.data) && node.data.some((d: any) => d?.fragments)) {
        ingestCollection(node);
        return;
      }
      for (const v of Object.values(node)) walk(v);
    };
    walk(annotationsObj);

    // Project the deduped cards onto document reading order. Cards whose anno
    // isn't found in the editor walk (orphaned marks, non-annoDataId fallback
    // ids) get Infinity and sink to the end; among equal ranks Array.sort is
    // stable (ES2019+), so they keep their original tag-walk order.
    return order
      .map((id) => grouped.get(id)!)
      .sort((a, b) => {
        const ia = annoOrderIndex.has(a.id) ? annoOrderIndex.get(a.id)! : Infinity;
        const ib = annoOrderIndex.has(b.id) ? annoOrderIndex.get(b.id)! : Infinity;
        return ia - ib;
      });
  }, [liveAnnotationsTags, taxonomyRoleLookup, subThemeSectionLookup, annoOrderIndex, annoGistIndex, annoActorNameIndex]);

  // Maps each annotation quote (annoDataId → the card's `id`) to the set of tag
  // `_id`s applied to it, read from the SAME collection the prevalence chart
  // renders its bars from (annotations.tags.tags.data, via the same fragment-id
  // derivation as `annos` above). The chart emits a bar's tag `_id` as the
  // filter value, so matching against this map filters to exactly the cards that
  // compose that bar — by `_id`, with no display-label fallback (labels aren't
  // unique across subThemes and can over-match).
  const cardTagIds = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const data: any[] = liveAnnotationsTags?.tags?.data || [];
    for (const tag of data) {
      const _id: string | undefined = tag?._id;
      if (!_id) continue; // chart keys bars by `_id`; without one it isn't filterable
      const fragments: any[] = Array.isArray(tag?.fragments) ? tag.fragments : [];
      for (const frag of fragments) {
        const id: string = frag?.annoDataId ?? frag?._id ?? `${_id}-${tag?.display ?? ""}`;
        let set = map.get(id);
        if (!set) map.set(id, (set = new Set<string>()));
        set.add(_id);
      }
    }
    return map;
  }, [liveAnnotationsTags]);

  const filteredAnnos: MappedAnno[] = useMemo(() => {
    const selected = Object.values(tagFilters);
    if (!selected.length) return annos;
    return annos.filter((anno) => {
      const ids = cardTagIds.get(anno.id);
      return selected.every((f) => (f.annoKeys ? f.annoKeys.includes(anno.id) : !!ids && ids.has(f._id)));
    });
  }, [annos, tagFilters, cardTagIds]);

  // Annos that pass the active filter (their editor marks stay highlighted;
  // everything else is dimmed). Memoised so the dimmer effect's deps are stable.
  const activeAnnoIds = useMemo(
    () => new Set(filteredAnnos.map((a) => a.id)),
    [filteredAnnos],
  );
  const hasTagFilter = Object.keys(tagFilters).length > 0;

  // Refs so the stable handleAnnotationClick (passed through the memoised editor
  // blockProps) can read the live filter state without changing identity.
  const activeAnnoIdsRef = useRef(activeAnnoIds);
  activeAnnoIdsRef.current = activeAnnoIds;
  const hasTagFilterRef = useRef(hasTagFilter);
  hasTagFilterRef.current = hasTagFilter;


  const documentTagsFields: DocumentTagsField[] = useMemo(() => {
    const out: DocumentTagsField[] = [];
    const seenTagPaths = new Set<string>();

    const labelForBlock = (block: any, fallback: string): string => {
      const fromProps =
        block?.props?.label || block?.props?.title || block?.label || block?.title;
      return fromProps ? String(fromProps) : fallback;
    };

    const SELF_MANAGED_BASE_CONFIGS = platformConfigs?.SELF_MANAGED_BASE_CONFIGS;

    const valueFor = (vp: string): string => {
      const v: any = getVal(contribution, vp);
      if (v === undefined || v === null) return "";
      if (typeof v === "string") return v;
      if (typeof v === "number" || typeof v === "boolean") return String(v);
      if (Array.isArray(v?.data)) {
        return v.data.map((d: any) => d?.display ?? d?.value ?? d?.label).filter(Boolean).join(", ");
      }
      if (typeof v?.display === "string") return v.display;
      if (typeof v?.value === "string") return v.value;
      return "";
    };

    /** Build a clickable link for a content-tag field (e.g. participantDetails
     *  → opens that participant's document). Mirrors V1's genLinkOrText —
     *  only fires for single-entry tag values on TagsInput* blocks. The
     *  target content type is read first from the entry's own `meta`
     *  (newly-saved entries with the meta-preservation fix in
     *  convertMultiValueToTagsValue) and falls back to the block's
     *  `props.tagType` — which is what the picker was configured with and
     *  equals the target content type for contentTags. Multi-entry tag
     *  fields render as comma-joined plain text (no link). The
     *  ?activeTab=<currentCT> suffix is added only in SM mode (matches V1
     *  behaviour: standalone doesn't guarantee a tab exists for each
     *  linked content type). */
    const NON_CONTENT_TAG_TYPES = new Set(["subThemes", "themes", "tags", "userTags"]);
    const linkFor = (vp: string, block: any): string | undefined => {
      // Gate strictly to tag-picker blocks; DatePicker / Dropdown* aren't
      // doc references and have no link semantics.
      if (block?.comp !== "TagsInputSingle" && block?.comp !== "TagsInputMulti") {
        return undefined;
      }
      const v: any = getVal(contribution, vp);
      const data: any[] = Array.isArray(v?.data) ? v.data : [];
      if (data.length !== 1) return undefined;
      const entry = data[0];
      const targetContentType: string | undefined =
        entry?.meta?.kp_content_type ?? entry?.meta?.content_type ?? block?.props?.tagType;
      const targetContentId: string | undefined = entry?._id;
      if (!targetContentType || !targetContentId) return undefined;
      // Skip well-known non-content tagTypes (sub-themes, themes, plain
      // tags, user tags) — those don't resolve via getRoutePathToPublishedContent.
      if (NON_CONTENT_TAG_TYPES.has(targetContentType)) return undefined;
      let link = getRoutePathToPublishedContent({
        contentType: targetContentType,
        contentId: targetContentId,
        SELF_MANAGED_BASE_CONFIGS,
      });
      if (link && SELF_MANAGED_BASE_CONFIGS?.enable && contentType) {
        const sep = link.includes("?") ? "&" : "?";
        link = `${link}${sep}activeTab=${contentType}`;
      }
      return link || undefined;
    };

    const walk = (blocks: any[]) => {
      if (!Array.isArray(blocks)) return;
      for (const b of blocks) {
        if (!b) continue;
        if (Array.isArray(b.blocks)) { walk(b.blocks); continue; }
        const vp: string = b.valuePath;
        if (!vp || seenTagPaths.has(vp)) continue;
        if (!DOC_TAG_COMP_SET.has(b.comp)) continue;
        const iconName = COMP_ICON_MAP[b.comp];
        if (!iconName) continue;
        const value = valueFor(vp);
        if (!value) continue;
        seenTagPaths.add(vp);
        const fallback = vp.split(".").pop() || vp;
        const link = linkFor(vp, b);
        out.push({
          iconName,
          iconWeight: COMP_ICON_WEIGHT_MAP[b.comp] as DocumentTagsField["iconWeight"],
          label: labelForBlock(b, fallback),
          value,
          ...(link ? { link } : {}),
        });
      }
    };

    const body = tpl?.kp_templates?.body;
    if (body?.subSpaces?.length) for (const sub of body.subSpaces) walk(sub?.configs);
    else walk(body?.configs);

    return out;
  }, [contribution, tpl, platformConfigs, contentType]);

  // Delete is now owned by the shared TplActions (rendered in the header), which
  // runs the native DeleteModal + access gating. V3 no longer has its own delete.

  // Single-doc AI annotate. Same /api/aiAnnotation/AIAutoAnnotate endpoint
  // the listing-page bulk action hits, scoped to one docId.
  const userData: any = useGetQueryData("userData");
  const SELF_MANAGED_BASE_CONFIGS: any = platformConfigs?.SELF_MANAGED_BASE_CONFIGS;
  const { SET_INFO_BANNER } = useBannerContext();
  // react-query types the untyped .js hook's mutate as `(vars: void) => ...`;
  // assert the real signature (same pattern as useWipeDocumentAnnotations below).
  const { mutate: triggerAIAutoAnnotate, status: aiAnnotateStatus } =
    useTriggerAIAutoAnnotate() as {
      mutate: (variables: any, options?: any) => void;
      status: string;
    };
  // Off-prod Signals-picker gate on the AI Annotate click. On prod
  // requestPickerThenRun runs the callback immediately (no modal); off-prod
  // it opens the modal, then invokes the callback with the run options
  // ({ allowedSignalTagIds, annotatorCall1Model }).
  const { requestPickerThenRun: requestSignalPicker, SignalPickerModalNode } =
    useSignalPicker();
  const canAIAnnotate: boolean =
    !!SELF_MANAGED_BASE_CONFIGS &&
    !!userData?.user &&
    __CheckAccessToModule(userData.user, "PUBLISH", "highlights");
  // Canonical doc-level "annotated" signal (mirrors backend definition in
  // buildHasAnnotationsStage / _getAnnotationStatusOverview).
  const isDocAnnotated: boolean =
    (contribution?.annotations?.tags?.tags?.data?.length || 0) > 0;
  // Whether annotation is possible on this contentType at all (tpl has annotation-
  // enabled blocks). Gates the "Clear annotations" action — clearing is about
  // removing annotations, independent of AI-annotate capability/permissions.
  const isAnnotationEnabledForContentType: boolean = !!tpl && isTplAnnotationEnabled(tpl);
  // annotate/clear act on the OG published doc; if a clone (edit/mod) is in
  // progress, republishing it overwrites these annotations — so only allow them
  // when the doc is cleanly published (same status signal TplActions gates on).
  const { isCleanPublished } = getPublishedDocState(
    (contributionQueryData as any)?.latestContent
  );
  const handleAIAnnotate = () => {
    if (aiAnnotateStatus === "loading") return;
    if (!contentType || !contribution?._id) return;
    requestSignalPicker((runOptions: Record<string, unknown> | undefined) => {
      triggerAIAutoAnnotate(
        {
          contentType,
          docIds: [contribution._id],
          ...(runOptions || {}),
        },
        {
          onSuccess: (res: any) => {
            const queued = res?.data?.summary?.jobsQueued;
            SET_INFO_BANNER(
              queued
                ? `Queued ${queued} annotation job${queued > 1 ? "s" : ""} for this document.`
                : "Annotation queued for this document.",
              "green",
              4000,
            );
          },
        },
      );
    });
  };

  // Single-doc "clear all annotations". Strips annoData + marks off the doc and
  // saves via the quickUpdate pipeline; downstream sync deletes the orphaned
  // annotations from the annotations collection + ES.
  const [showClearAnnoModal, setShowClearAnnoModal] = useState<boolean>(false);
  // Bumped after a clear to force the body field editors to re-mount. The read-
  // only Lexical editors keep their initial editorState (marks are imperative,
  // via the anno registry), so refetching the contribution refreshes the sidebar
  // but won't drop the already-painted highlight marks without a re-mount.
  const [bodyVersion, setBodyVersion] = useState<number>(0);
  // react-query types mutate as `(vars: void) => ...` here; assert the real signature.
  const { mutate: wipeDocumentAnnotations, status: clearAnnoStatus } =
    useWipeDocumentAnnotations() as {
      mutate: (variables: any, options?: any) => void;
      status: string;
    };
  const handleClearAnnotations = () => {
    setShowClearAnnoModal(false);
    if (clearAnnoStatus === "loading") return;
    if (!contentType || !contribution?._id) return;
    wipeDocumentAnnotations(
      { contentType, contentId: contribution._id },
      {
        onSuccess: async (res: any) => {
          // The doc was cleared synchronously by quickUpdateContent. Refetch the
          // contribution (await so the cleared editorState is in cache) — this
          // refreshes isDocAnnotated + the sidebar cards...
          await queryClient.invalidateQueries(["Contributions"]);
          // ...then re-mount the body editors so their painted marks drop too.
          setBodyVersion((v) => v + 1);
          const removed = res?.data?.annotationsRemoved;
          SET_INFO_BANNER(
            removed
              ? `Cleared ${removed} annotation${removed > 1 ? "s" : ""} from this document.`
              : "Annotations cleared from this document.",
            "green",
            4000,
          );
        },
      },
    );
  };

  const [theme, setTheme] = useState<ThemeName>("Frost");
  const [layout, setLayout] = useState<"A" | "B">("B");
  const [fieldsFilter, setFieldsFilter] = useState<"all" | "richtext">("all");
  const [viewControlsOpen, setViewControlsOpen] = useState<boolean>(false);
  const viewControlsEnabled: boolean = !isFeatureDisabledOnProd("publishedPageV3ViewControls", {
    disabledFeatures: platformConfigs?.FEATURES_DISABLED_ON_PROD || [],
    isProd: import.meta.env.VITE_ENV === "prod",
    tenant: getTenant(),
  });
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("annotations");
  const [rightTab, setRightTab] = useState<RightTab>("distribution");
  // Where the Distribution widget renders: the left sidebar card, or a tab in
  // the right-hand AI panel. Toggled from the View settings modal. Defaults to
  // "panel" (the "Summary panel" option) so Distribution lives in the AI panel.
  const [distributionPlacement, setDistributionPlacement] = useState<"sidebar" | "panel">("panel");
  const handleSetDistributionPlacement = useCallback((v: "sidebar" | "panel") => {
    setDistributionPlacement(v);
    // The Distribution tab only exists in "panel" mode — keep rightTab valid:
    // select it when enabling panel placement, fall back to Summary otherwise.
    setRightTab((prev) =>
      v === "panel" ? "distribution" : prev === "distribution" ? "summary" : prev,
    );
    // Summary fetch arming is handled by the effect that watches
    // (aiPanelExpanded && rightTab === "summary"); no imperative arm needed here.
  }, []);
  const [selectedAnnoId, setSelectedAnnoId] = useState<string | null>(null);
  const [expandedAIPanel, setExpandedAIPanel] = useState<boolean>(false);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const panelSlotRef = useRef<HTMLDivElement | null>(null);
  const [gridWidth, setGridWidth] = useState(0);
  const [slotHeight, setSlotHeight] = useState(48);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 800,
  );

  useLayoutEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const sidebarColRef = useRef<HTMLDivElement | null>(null);
  const sidebarScrollRef = useRef<HTMLDivElement | null>(null);
  const [sidebarScrollMax, setSidebarScrollMax] = useState<number>(200);

  useLayoutEffect(() => {
    // collabPending: loader is up until it flips false, so the refs below are
    // null on the "success" run — re-fire once the real layout mounts.
    if (getContributionStatus !== "success" || collabPending) return;
    const col = sidebarColRef.current;
    const scroller = sidebarScrollRef.current;
    if (!col || !scroller) return;
    const measure = () => {
      const colRect = col.getBoundingClientRect();
      const scRect = scroller.getBoundingClientRect();
      const offsetInsideCol = scRect.top - colRect.top;
      // Reserve a little space below the scroller so its track clears the card's
      // rounded bottom corner. The 16px content padding now lives inside the
      // scroller, so this is smaller than before.
      const BOTTOM_PAD = 5;
      const colMax = Math.max(viewportHeight - 78 - 14, 200);
      setSidebarScrollMax(Math.max(colMax - offsetInsideCol - BOTTOM_PAD, 120));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(col);
    ro.observe(scroller);
    Array.from(col.children).forEach((child) => ro.observe(child as Element));
    return () => ro.disconnect();
  }, [getContributionStatus, collabPending, viewportHeight, sidebarTab]);

  const selectedAnnoIdRef = useRef<string | null>(null);
  useEffect(() => { selectedAnnoIdRef.current = selectedAnnoId; }, [selectedAnnoId]);

  // Smooth-scroll the sidebar so the given card is vertically centred.
  const centerCardInSidebar = React.useCallback((cardEl: HTMLElement) => {
    const scroller = sidebarScrollRef.current;
    if (!cardEl || !scroller) return;
    const cardRect = cardEl.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const offsetFromScrollerTop = cardRect.top - scRect.top + scroller.scrollTop;
    const target = offsetFromScrollerTop - scRect.height / 2 + cardRect.height / 2;
    scroller.scrollTo({ top: target, behavior: "smooth" });
  }, []);

  // Centre a card whose sidebar list was just (re)rendered — e.g. after a
  // dimmed-mark click clears the filter and the hidden cards remount. Their
  // quotes hydrate (Lexical) over several frames, growing the cards above the
  // target and shifting its offset, so a one-shot scroll lands wrong. Wait for
  // the target's content offset to stop changing between frames, then centre
  // once. Frame-capped so it can't spin forever.
  const centerCardInSidebarWhenSettled = React.useCallback((id: string) => {
    const safe = id.replace(/"/g, '\\"');
    let frames = 0;
    let lastOffset = NaN;
    const tick = () => {
      const scroller = sidebarScrollRef.current;
      const card = gridRef.current?.querySelector<HTMLElement>(`[data-anno-id="${safe}"]`) ?? null;
      if (!scroller || !card) {
        if (frames++ < 60) requestAnimationFrame(tick);
        return;
      }
      const cardRect = card.getBoundingClientRect();
      const scRect = scroller.getBoundingClientRect();
      const offset = cardRect.top - scRect.top + scroller.scrollTop;
      if (Math.abs(offset - lastOffset) < 1 || frames++ >= 60) {
        centerCardInSidebar(card);
        return;
      }
      lastOffset = offset;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [centerCardInSidebar]);

  const handleSelectAnno = React.useCallback((id: string, cardEl: HTMLElement | null, opts?: { settleSidebar?: boolean }) => {
    setSelectedAnnoId(id);
    // Re-center on every explicit click — including re-selecting the anno that's
    // already focused — so if the user scrolled the card (here) or the mark
    // (below) out of view, clicking either brings it back.
    if (opts?.settleSidebar) {
      // List just re-rendered (filter cleared); wait for it to settle.
      centerCardInSidebarWhenSettled(id);
    } else if (cardEl) {
      centerCardInSidebar(cardEl);
    }
    // Smooth-scroll the mark into the area BELOW the sticky header. We can't
    // use scrollIntoView's "center" alone because it ignores the sticky header
    // and may park the mark behind it; instead we find the actual scroll
    // container (window or a scrollable ancestor) and offset manually.
    requestAnimationFrame(() => {
      const safe = id.replace(/"/g, '\\"');
      // Marks carry `id=<annoDataId>` (stamped by AnnoPlugin's mutation
      // listener). te-mark/data-anno-ids are gone (that was the removed dimming
      // layer).
      const mark =
        gridRef.current?.querySelector<HTMLElement>(`mark[id="${safe}"]`) ?? null;
      if (!mark) return;
      const HEADER_OFFSET = 78;

      const findScroller = (el: HTMLElement): HTMLElement | null => {
        let p: HTMLElement | null = el.parentElement;
        while (p) {
          const s = getComputedStyle(p);
          if (/(auto|scroll|overlay)/.test(s.overflowY) && p.scrollHeight > p.clientHeight) {
            return p;
          }
          p = p.parentElement;
        }
        return null;
      };

      const scroller = findScroller(mark);
      const markRect = mark.getBoundingClientRect();

      if (scroller) {
        const scRect = scroller.getBoundingClientRect();
        const visibleTop = Math.max(scRect.top, HEADER_OFFSET);
        const visibleBottom = scRect.bottom;
        const available = Math.max(visibleBottom - visibleTop, 100);
        const desiredTop = visibleTop + available / 2 - markRect.height / 2;
        const delta = markRect.top - desiredTop;
        scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: "smooth" });
      } else {
        const available = Math.max(window.innerHeight - HEADER_OFFSET, 100);
        const desiredTop = HEADER_OFFSET + available / 2 - markRect.height / 2;
        const delta = markRect.top - desiredTop;
        const root = document.scrollingElement || document.documentElement;
        const currentTop = root.scrollTop || window.scrollY;
        (root as any).scrollTo
          ? root.scrollTo({ top: currentTop + delta, behavior: "smooth" })
          : window.scrollTo({ top: currentTop + delta, behavior: "smooth" });
      }
    });
  }, [centerCardInSidebar, centerCardInSidebarWhenSettled]);

  // V1-routed mark click: V1's AnnoPlugin calls this with the clicked
  // annoDataId (when v2 + onAnnotationClick are set). Find the AnnoCard
  // wrapper by its data-anno-id and reuse handleSelectAnno.
  const handleAnnotationClick = React.useCallback((id: string | null) => {
    // Outside-click deselect, reported by the editor (AnnoPlugin) → clear focus.
    // The blue mark clears via the AnnoCard's focusMark cleanup when selection drops.
    if (!id) {
      setSelectedAnnoId(null);
      return;
    }
    const select = (settleSidebar = false) => {
      const card =
        gridRef.current?.querySelector<HTMLElement>(
          `[data-anno-id="${id.replace(/"/g, '\\"')}"]`,
        ) ?? null;
      handleSelectAnno(id, card, { settleSidebar });
    };
    // Clicking a dimmed mark (filter applied + this anno filtered out) clears
    // the filter first, then proceeds with the normal select. Its sidebar card
    // is hidden while filtered, so defer a frame to let it remount, and use the
    // settle-aware centering (the remounted cards' quotes hydrate over several
    // frames, shifting layout — a one-shot scroll would land wrong).
    if (hasTagFilterRef.current && !activeAnnoIdsRef.current.has(id)) {
      setTagFilters({});
      requestAnimationFrame(() => select(true));
      return;
    }
    select();
  }, [handleSelectAnno]);

  // Deep-link landing: when arriving with `?scrollIntoViewElemId=…` (from the
  // Annotation Explorer = annoKey, or an AI Chat chunk reference = valuePath),
  // scroll to and focus that annotation/field once the content has rendered.
  useScrollToDeepLinkTarget({
    status: getContributionStatus,
    search: location.search,
    gridRef,
    onAnnotation: handleAnnotationClick,
  });

  // Reverse direction: clicking an annotation mark in the transcript selects
  // and centres its corresponding sidebar card. Scoped to the V3 content grid
  // (where both marks and sidebar cards live) rather than `document`, so
  // we're not handling click events from the rest of the app.
  useEffect(() => {
    // collabPending: gridRef is null while the loader is up — re-fire on mount.
    if (getContributionStatus !== "success" || collabPending) return;
    const root = gridRef.current;
    if (!root) return;

    let pending: ReturnType<typeof setTimeout> | null = null;
    const cancel = () => { if (pending) { clearTimeout(pending); pending = null; } };

    const onClick = (e: MouseEvent) => {
      // Bail on the 2nd+ click of a dbl/triple click — that's a selection
      // gesture, not an anno-pick.
      if (e.detail > 1) { cancel(); return; }
      const target = e.target as HTMLElement | null;
      const mark = target?.closest?.("mark.te-mark") as HTMLElement | null;
      if (!mark) return;
      const idsAttr = mark.getAttribute("data-anno-ids") ?? "";
      const id = idsAttr.split(/\s+/).filter(Boolean)[0];
      if (!id) return;
      const card = root.querySelector<HTMLElement>(`[data-anno-id="${id.replace(/"/g, '\\"')}"]`);
      // Defer briefly: if a 2nd click lands (becoming a dblclick) or a text
      // selection is made, cancel — otherwise it's a real anno-pick click.
      cancel();
      pending = setTimeout(() => {
        pending = null;
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && sel.toString().length > 0) return;
        handleSelectAnno(id, card);
      }, 250);
    };
    const onDbl = () => cancel();
    root.addEventListener("click", onClick);
    root.addEventListener("dblclick", onDbl);
    return () => {
      root.removeEventListener("click", onClick);
      root.removeEventListener("dblclick", onDbl);
      cancel();
    };
  }, [handleSelectAnno, getContributionStatus, collabPending]);

  // Outside-click deselect is handled inside the shared editor's AnnoPlugin
  // (the same document listener that clears focus on the non-V3 published page),
  // routed here via onAnnotationClick(null) → handleAnnotationClick. No separate
  // host-side listener + skip-list needed.

  useLayoutEffect(() => {
    // collabPending in the gate + deps: while it's true the loader is up and
    // gridRef is null, so running here would no-op and never re-fire — the
    // grid mounts only after collabPending flips false.
    if (getContributionStatus !== "success" || collabPending) return;
    const grid = gridRef.current;
    const slot = panelSlotRef.current;
    if (!grid || !slot) return;
    const measure = () => {
      setGridWidth(grid.getBoundingClientRect().width);
      setSlotHeight(Math.max(viewportHeight - 82 - 14, 200));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(grid);
    ro.observe(slot);
    return () => ro.disconnect();
  }, [getContributionStatus, collabPending, viewportHeight]);

  const GAP = 14;
  const PADDING = 14 * 2;
  const innerWidth = Math.max(gridWidth - PADDING - GAP, 48);
  const expandedRightW = innerWidth * 0.25;
  const expandedLeftW = innerWidth - expandedRightW;

  const rightColW = useMotionValue(48);
  const panelH = useMotionValue(48);
  const gridTemplate = useTransform(rightColW, (w: number) => `1fr ${w}px`);

  // When the Distribution widget lives in the AI panel (placement === "panel"),
  // the panel must stay open — its content can't be collapsed behind the Sparkle
  // toggle. Force it expanded regardless of the manual toggle state.
  const aiPanelExpanded = distributionPlacement === "panel" || expandedAIPanel;

  // Lazy summary arm, driven by state (not a click handler): the summary fetch
  // fires whenever the AI panel is open AND the Summary tab is the active one —
  // regardless of HOW it got there (default landing, placement toggle, or tab
  // click). setSummaryRequested only latches false→true, so this runs once.
  useEffect(() => {
    if (aiPanelExpanded && rightTab === "summary") setSummaryRequested(true);
  }, [aiPanelExpanded, rightTab]);

  const measuredRef = useRef(false);
  useEffect(() => {
    if (gridWidth === 0) return;
    const targetW = aiPanelExpanded ? expandedRightW : 48;
    const targetH = aiPanelExpanded ? slotHeight : 48;
    if (!measuredRef.current) {
      rightColW.set(targetW);
      panelH.set(targetH);
      measuredRef.current = true;
      return;
    }
    const opts = { duration: 0.4, ease: [0.32, 0.72, 0, 1] as const };
    const a1 = animate(rightColW, targetW, opts);
    const a2 = animate(panelH, targetH, opts);
    return () => { a1.stop(); a2.stop(); };
  }, [aiPanelExpanded, expandedRightW, slotHeight, gridWidth, rightColW, panelH]);

  // The doc isn't ready when the fetch resolves — it's ready when the fetch has
  // resolved AND the collab session that owns its fields is live. Releasing on
  // the fetch alone renders every field read-only (no collabEditValue yet) and
  // hides empty editable blocks, then flips to the editable layout a moment
  // later — the read-only flash, plus a reflow that moves the page under anyone
  // who has already scrolled. collabPending is false wherever collab is off, so
  // non-collab docs are unaffected.
  if (getContributionStatus === "loading" || collabPending) {
    return (
      <ThemeRoot theme={theme}>
        <LoaderCircleV2 S />
      </ThemeRoot>
    );
  }
  if (getContributionStatus === "error") {
    return (
      <ThemeRoot theme={theme}>
        <ErrorStates errorResponseObject={contributionError as any} />
      </ThemeRoot>
    );
  }
  if (getContributionStatus !== "success") return null;

  const headerTitleInput = (
    <InlineEditableInput
      id="publishedPageV3HeaderTitle"
      value={headerTitle}
      placeholder="Enter a title"
      // matches the sibling crumb's typography + theme colour
      typoClassName="UI_TITLE"
      style={{ color: t("grey80") }}
      readOnly={!collabEditValue?.canEdit}
      // controlled input: fires per keystroke, so peers see letters stream;
      // the comp's blur semantics (trim, empty-revert) also flow through here.
      onChange={handleTitleChange}
    />
  );
  // In collab, the title is a field like any other: focus/blur on the editable
  // node propagates up to the wrapper, which owns the lock bracket and the
  // locked-by-other overlay. Outside collab there is no lock to hold, so the
  // read-only title renders bare.
  const headerTitleCrumb = (
    <CollabLockedField
      session={collabEditValue}
      valuePath={TITLE_VALUE_PATH}
      // one-line crumb: the absolute badge would land on the title text
      compactChip
      // a crumb, not a full-width field block: keep it shrinkable so the title
      // still truncates inside BreadcrumbHeader's width cap
      style={{ display: "flex", minWidth: 0 }}
    >
      {headerTitleInput}
    </CollabLockedField>
  );

  return (
    <DocCollaborationContext.Provider value={collabModeEnabled ? null : docCollaboration}>
    <AnnotationEditPipelineContext.Provider value={collabModeEnabled ? null : annotationEditPipeline}>
    <CollabEditContext.Provider value={collabEditValue}>
    <AnnoEditorRegistryProvider>
    <AnnoFilterDimmer annos={annos} activeAnnoIds={activeAnnoIds} hasFilter={hasTagFilter} />
    <ThemeRoot theme={theme}>
        {/* Header */}
        <HeaderContainer>
            <HeaderTitleContainer>
                <ButtonGhostV2 icon="CaretLeft" iconColor={t("grey80")} onClick={handleBack} style={{ padding: 4 }} />
                <HeaderInfoContainer>
                    <BreadcrumbHeader
                        items={[
                          { display: contentTypeDisplay, onClick: handleBack },
                          { node: headerTitleCrumb },
                        ]}
                        color={t("grey80")}
                        mutedColor={t("grey40")}
                    />
                    <HeaderSubtitle>
                        {author && <span>{author}</span>}
                        {author && dateCreatedDisplay && <span style={{ color: t('grey20'), fontSize: '12px' }}>●</span>}
                        {dateCreatedDisplay && <span>{dateCreatedDisplay}</span>}
                    </HeaderSubtitle>
                </HeaderInfoContainer>
            </HeaderTitleContainer>
            <HeaderActionsContainer>
                {/* Collab save status — ContributeTpl's autosave indicator,
                    driven by the flush protocol (setField → Saving…, flush:ok
                    for our pending paths → Saved, flush:error → red). Leftmost
                    of the right-side actions. */}
                {collabEditValue && <CollabSaveStatus saveStatus={saveStatus} />}
                {/* layout / fields / theme / switch-to-old-UI live in the
                    floating View-settings modal (bottom-right) to keep the
                    header clean. */}
                {/* <ButtonTertiaryCompact icon="Sparkle" iconWeight="bold" color={t("grey80")}>Auto Highlight</ButtonTertiaryCompact> */}
                {/* <ButtonTertiaryCompact icon="ShareNetwork" iconWeight="bold" color={t("grey80")}>Share</ButtonTertiaryCompact> */}
                {canAIAnnotate && (
                  // Disabled buttons swallow hover events (pointer-events: none),
                  // so the tooltip-on-disabled trick is to wrap the button in a
                  // span the tooltip can anchor to. Only show the tooltip when
                  // disabled — no need for hover noise on the live button.
                  !isCleanPublished ? (
                    <TooltipV2
                      text="This document is being edited or moderated — annotations would be overwritten."
                      showPointer={true}
                      placement="bottom"
                    >
                      <span style={{ display: "inline-flex" }}>
                        <ButtonTertiaryCompact
                          icon="HighlighterCircle"
                          iconWeight="bold"
                          color={t("grey80")}
                          disabled
                        >AI Annotate</ButtonTertiaryCompact>
                      </span>
                    </TooltipV2>
                  ) : isDocAnnotated ? (
                    <TooltipV2
                      text="This document is already annotated."
                      showPointer={true}
                      placement="bottom"
                    >
                      <span style={{ display: "inline-flex" }}>
                        <ButtonTertiaryCompact
                          icon="HighlighterCircle"
                          iconWeight="bold"
                          color={t("grey80")}
                          disabled
                        >AI Annotate</ButtonTertiaryCompact>
                      </span>
                    </TooltipV2>
                  ) : (
                    <ButtonTertiaryCompact
                      icon="HighlighterCircle"
                      iconWeight="bold"
                      color={t("grey80")}
                      disabled={aiAnnotateStatus === "loading"}
                      onClick={handleAIAnnotate}
                    >AI Annotate</ButtonTertiaryCompact>
                  )
                )}
                {isAnnotationEnabledForContentType && (
                  // Shown whenever annotation is possible on this contentType.
                  // Enabled only when there's something to clear; disabled (with
                  // tooltip) when the doc has no annotations or is being edited/moderated.
                  !isCleanPublished ? (
                    <TooltipV2
                      text="This document is being edited or moderated — clearing would be overwritten."
                      showPointer={true}
                      placement="bottom"
                    >
                      <span style={{ display: "inline-flex" }}>
                        <ButtonTertiaryCompact
                          icon="XCircle"
                          iconWeight="bold"
                          color={t("grey80")}
                          disabled
                        >Clear Annotations</ButtonTertiaryCompact>
                      </span>
                    </TooltipV2>
                  ) : isDocAnnotated ? (
                    <ButtonTertiaryCompact
                      icon="XCircle"
                      iconWeight="bold"
                      color={t("grey80")}
                      disabled={clearAnnoStatus === "loading"}
                      onClick={() => setShowClearAnnoModal(true)}
                    >Clear Annotations</ButtonTertiaryCompact>
                  ) : (
                    <TooltipV2
                      text="No annotations to clear."
                      showPointer={true}
                      placement="bottom"
                    >
                      <span style={{ display: "inline-flex" }}>
                        <ButtonTertiaryCompact
                          icon="XCircle"
                          iconWeight="bold"
                          color={t("grey80")}
                          disabled
                        >Clear Annotations</ButtonTertiaryCompact>
                      </span>
                    </TooltipV2>
                  )
                )}
                {/* Edit / Review / Delete come from the shared TplActions, which
                    owns all the access + status gating (so V3 gets full parity with
                    V2). publishedPageV3Actions renders them as inline compact buttons.
                    selfServeActions makes edit/delete run inline for participants.
                    On collab-owned docs the legacy Edit/Review entry points are
                    hidden — those flows are rejected server-side. */}
                <TplActions
                  content={contribution}
                  publishedPageV3Actions
                  selfServeActions={selfServeActions}
                  suppressLegacyLifecycleActions={collabOwnsThisDoc}
                />
            </HeaderActionsContainer>
        </HeaderContainer>

        {/* Content */}
        <ContentGrid ref={gridRef} style={{ gridTemplateColumns: gridTemplate }}>
            <ContentGrid2 style={{ maxWidth: expandedLeftW }}>
                <Column>
                    {layout === "A" && documentTagsFields.length > 0 && (
                        <Card>
                            <DocumentTagsCard fields={documentTagsFields} />
                        </Card>
                    )}
                    <Card>
                        {V3_SUPPORTED_LAYOUTS.has(tpl?.category) ? (
                            <FieldsRenderer
                                key={`v3-body-${bodyVersion}`}
                                tpl={tpl}
                                content={liveContribution}
                                blockProps={{
                                    v2: true,
                                    onAnnotationClick: handleAnnotationClick,
                                    // (card→mark highlight is driven imperatively
                                    // via the registry's focusMark — a reactive
                                    // prop here is swallowed by ViewBlockGenerator's
                                    // memo)
                                }}
                                shouldRenderBlock={(block: any) => {
                                    // Separated-metadata (layout A): quant /
                                    // categorical / tag fields live in the top
                                    // DocumentTagsCard, not the body.
                                    if (
                                        layout === "A" &&
                                        (DOC_TAG_COMP_SET.has(block.comp) ||
                                            isTagPath(block.valuePath))
                                    )
                                        return false;
                                    // Richtext-only field filter.
                                    if (
                                        fieldsFilter === "richtext" &&
                                        !(blockRegistry as any).hasCapability?.(
                                            block.comp,
                                            "annotation",
                                        )
                                    )
                                        return false;
                                    return true;
                                }}
                            />
                        ) : (
                            <div style={{ padding: "2rem" }}>
                                <UI_BODY_SM_DF>
                                    This layout isn’t supported in the new published view yet.
                                </UI_BODY_SM_DF>
                            </div>
                        )}
                    </Card>
                </Column>
                <Column ref={sidebarColRef} style={{ position: "sticky", top: 82, alignSelf: "start", maxHeight: `${Math.max(viewportHeight - 82 - 14, 200)}px` }}>
                    {hasPrevalenceData && distributionPlacement === "sidebar" && (
                        <Card>
                            <DistributionCard contribution={contributionForDistribution} activeTagId={activeFilterTagId} onSelectTag={handleSelectTag} />
                        </Card>
                    )}
                    <Card style={{ padding: 0 }}>
                        <Tabs<SidebarTab>
                            value={sidebarTab}
                            onChange={setSidebarTab}
                            tabs={
                                hasTagFilter
                                    ? [
                                          {
                                              value: "annotations",
                                              label: `Filter: ${Object.values(tagFilters)
                                                  .map((f) => f.tagLabel)
                                                  .join(", ")}`,
                                              icon: "Funnel",
                                          },
                                      ]
                                    : [
                                          { value: "annotations", label: "Annotations", icon: "HighlighterCircle" },
                                          // { value: "comments", label: "Comments", icon: "Chat" },
                                      ]
                            }
                            padding="16px"
                            scrollRef={sidebarScrollRef}
                            scrollMaxHeight={sidebarScrollMax}
                            actions={
                                hasTagFilter ? (
                                    <ButtonTertiaryCompact
                                        icon="X"
                                        iconWeight="bold"
                                        onClick={() => setTagFilters({})}
                                    >
                                        Clear Filter
                                    </ButtonTertiaryCompact>
                                ) : undefined
                            }
                        >
                            {sidebarTab === "annotations" && (
                                <HighlightsList>
                                    {filteredAnnos.length === 0 ? (
                                        <div style={{ color: t("grey60") }}>
                                            <UI_BODY_SM_DF>
                                                {hasTagFilter
                                                    ? "No annotations match the selected filters."
                                                    : "No annotations yet."}
                                            </UI_BODY_SM_DF>
                                        </div>
                                    ) : (
                                        filteredAnnos.map((anno) => (
                                            <AnnoCardWithScroll
                                                key={anno.id}
                                                anno={anno}
                                                selected={selectedAnnoId === anno.id}
                                                onSelect={handleSelectAnno}
                                                onCollapse={() => setSelectedAnnoId(null)}
                                                tagDetailsExpanded={tagDetailsExpanded}
                                                onToggleTagDetails={() =>
                                                    setTagDetailsExpanded((p: boolean) => !p)
                                                }
                                            />
                                        ))
                                    )}
                                </HighlightsList>
                            )}
                            {/* {sidebarTab === "comments" && (
                                <SidebarScroller ref={sidebarScrollRef} style={{ maxHeight: sidebarScrollMax }}>
                                    <SummaryText style={{ whiteSpace: "pre-wrap" }}>
                                    </SummaryText>
                                </SidebarScroller>
                            )} */}
                        </Tabs>
                    </Card>
                </Column>
            </ContentGrid2>
            <div style={{ position: "sticky", top: 82, alignSelf: "start", width: "100%" }}>
            <div ref={panelSlotRef} style={{ position: "relative", width: "100%", minHeight: 48 }}>
                <Panel
                    style={{ position: "absolute", top: 0, right: 0, width: "100%", height: panelH }}
                >
                    {distributionPlacement !== "panel" && (
                        <IconContainer
                            onClick={() => setExpandedAIPanel((p) => !p)}
                            animate={{ y: expandedAIPanel ? 8 : 0 }}
                            transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
                        >
                            {expandedAIPanel ? <CaretRight size={16} weight="bold" color={t("grey80")} /> : <Sparkle size={16} weight="bold" color={t("grey80")} />}
                        </IconContainer>
                    )}
                    <AnimatePresence>
                        {aiPanelExpanded && (
                            <motion.div
                                key="ai-panel-content"
                                style={{ width: expandedRightW, flexShrink: 0 }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                            >
                                <Tabs<RightTab>
                                    value={rightTab}
                                    onChange={setRightTab}
                                    tabs={
                                        distributionPlacement === "panel"
                                            ? [
                                                  { value: "distribution", label: "Distribution", icon: ChartBarHorizontalIcon },
                                                  { value: "summary", label: "Summary", icon: "Sparkle" },
                                              ]
                                            : [{ value: "summary", label: "Summary", icon: "Sparkle" }]
                                    }
                                    padding="16px"
                                >
                                    {rightTab === "distribution" && (
                                        <DistributionCardContent
                                            contribution={contributionForDistribution}
                                            activeTagId={activeFilterTagId}
                                            onSelectTag={handleSelectTag}
                                        />
                                    )}
                                    {rightTab === "summary" && (
                                        <AISummaryPanel summary={aiSummary} isLoading={isSummaryLoading} tooShort={aiSummaryTooShort} />
                                    )}
                                </Tabs>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Panel>
            </div>
            </div>
        </ContentGrid>
        {aiAnnotateStatus === "loading" && (
          <LoaderOverlay loaderText="Queueing annotation..." />
        )}
        {showClearAnnoModal && (
          <ModalConfirm
            title="Clear all annotations from this document?"
            subTitle="This permanently removes every annotation (human and AI) on this document, and cannot be undone."
            onClose={() => setShowClearAnnoModal(false)}
            onConfirm={handleClearAnnotations}
            onConfirmText="Clear annotations"
          />
        )}
        {clearAnnoStatus === "loading" && (
          <LoaderOverlay loaderText="Clearing annotations..." />
        )}
        {SignalPickerModalNode}

        {/* Floating View-settings button — sits to the right of the localhost
            tenant switcher (which is fixed at bottom: 7px; right: 60px). Opens
            the modal holding layout / fields / theme / switch-to-old-UI. */}
        {viewControlsEnabled && (
          <FloatingControlsButton
            type="button"
            onClick={() => setViewControlsOpen(true)}
            aria-label="View settings"
          >
            <Faders size={18} weight="bold" color={t("grey80")} />
          </FloatingControlsButton>
        )}
        {viewControlsEnabled && viewControlsOpen && (
          <ViewControlsModal
            onDownloadAnnotations={handleDownloadAnnotations}
            downloadPending={pendingCsvExport}
            layout={layout}
            setLayout={setLayout}
            theme={theme}
            setTheme={setTheme}
            fieldsFilter={fieldsFilter}
            setFieldsFilter={setFieldsFilter}
            distributionPlacement={distributionPlacement}
            setDistributionPlacement={handleSetDistributionPlacement}
            onClose={() => setViewControlsOpen(false)}
          />
        )}
    </ThemeRoot>
    </AnnoEditorRegistryProvider>
    </CollabEditContext.Provider>
    </AnnotationEditPipelineContext.Provider>
    </DocCollaborationContext.Provider>
  );
};

export default PublishedPageV3;
